import { ComponentProps, RefObject } from 'react';

import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover';

interface ContinueAnywayPopoverProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  /** Primary-button label; defaults to "Continue anyway" (Save & Exit passes "Save anyway"). */
  confirmLabel?: string;
  anchorRef?: RefObject<HTMLElement | null>;
}

/** "Continue anyway" / "Save anyway" confirmation anchored to a wizard action button. */
export const ContinueAnywayPopover = ({
  open,
  onOpenChange,
  onConfirm,
  confirmLabel,
  anchorRef,
}: ContinueAnywayPopoverProps) => {
  const { t } = useTranslation();

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverAnchor virtualRef={anchorRef as ComponentProps<typeof PopoverAnchor>['virtualRef']} />
      <PopoverContent
        align="end"
        side="bottom"
        className="flex w-fit max-w-96 min-w-80 flex-col gap-8 p-6 shadow-lg"
      >
        {/* Cap the copy at the popover's base inner width (min-w-80 320px − p-6 48px) so only a
            long CTA row — never the paragraph — grows the popover past 320px (keeps EN unchanged). */}
        <div className="w-68 space-y-2">
          <h3 className="text-lg leading-none font-semibold tracking-tight text-foreground">
            {t('campaigns.setup.continue_anyway_title')}
          </h3>
          <p className="text-base leading-6 font-normal text-foreground-muted">
            {t('campaigns.setup.continue_anyway_body')}
          </p>
        </div>
        <div className="flex justify-end gap-2">
          <Button
            variant="ghost-neutral"
            onClick={() => onOpenChange(false)}
            className="min-w-0 shrink-0"
          >
            {t('common.cancel')}
          </Button>
          <Button onClick={onConfirm} className="min-w-0 shrink-0">
            {confirmLabel ?? t('campaigns.setup.continue_anyway_confirm')}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};
